import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FileText, Search, Car, User, Calendar, DollarSign, Printer, Download, CheckCircle2, ArrowLeft, Save, Trash2, Plus, PenTool, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

// Full ADAS system labels — mirrors VinDecoder for consistent look/feel.
const ADAS_LABELS = {
  front_camera:            { name: "Front Camera",             icon: "📷" },
  front_radar_acc:         { name: "Front Radar (ACC)",        icon: "📡" },
  rear_radar_bsm:          { name: "Rear Radar (Blind Spot)",  icon: "🛡️" },
  surround_view_360:       { name: "360° Surround Cameras",    icon: "🎥" },
  park_sensors_ultrasonic: { name: "Park Sensors",             icon: "🅿️" },
  lane_departure_warning:  { name: "Lane Departure Warning",   icon: "↔️" },
  lane_keep_assist:        { name: "Lane Keep Assist",         icon: "🛣️" },
  auto_emergency_braking:  { name: "Auto Emergency Brake",     icon: "🛑" },
  adaptive_headlamps:      { name: "Adaptive Headlamps",       icon: "💡" },
  hud_head_up_display:     { name: "Head-Up Display",          icon: "🪟" },
  night_vision:            { name: "Night Vision",             icon: "🌙" },
  steering_angle_sensor:   { name: "Steering Angle Sensor",    icon: "🎯" },
  traffic_sign_recognition:{ name: "Traffic Sign Recognition", icon: "🚸" },
};

const CAL_TYPES = [
  { key: "static",  label: "Static",  color: "text-cyan-300",    dot: "bg-cyan-400" },
  { key: "dynamic", label: "Dynamic", color: "text-fuchsia-300", dot: "bg-fuchsia-400" },
  { key: "dual",    label: "Dual",    color: "text-amber-300",   dot: "bg-amber-400" },
];

export default function QuoteNew() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [vinInput, setVinInput] = useState("");
  const [decoding, setDecoding] = useState(false);
  const [decoded, setDecoded] = useState(null);
  const [catalog, setCatalog] = useState({ operations: [], types: [], prices: {} });
  const [customServices, setCustomServices] = useState([]);
  const [manualName, setManualName] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [pendingCustom, setPendingCustom] = useState(null);
  const [salespeople, setSalespeople] = useState([]);
  const [technicians, setTechnicians] = useState([]);

  // Selected ADAS ops: { [op_key]: { cal_type, unit_price, amount } }
  const [selected, setSelected] = useState({});

  const [form, setForm] = useState({
    vin: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    is_body_shop: false,
    linked_customer_id: "",
    linked_body_shop_id: "",
    tax_pct: 0,
    notes: "",
    salesperson_id: "",
    salesperson_name: "",
    commission_pct: 5,
    scheduled_at: "",
    technician_id: "",
    technician_name: "",
    status: "draft",
  });

  const [contactOptions, setContactOptions] = useState([]); // customers + body shops merged
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");

  const [quoteRef, setQuoteRef] = useState(null); // full quote when editing

  // ---- Load catalog + salespeople + technicians ----
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/quotes/adas-defaults");
        setCatalog(r.data);
      } catch (e) { /* noop */ }
      try {
        const r = await api.get("/quotes/custom-services");
        setCustomServices(r.data || []);
      } catch (e) { /* noop */ }
      try {
        // Try the non-admin roster first (works for sales/technician users
        // creating their own quote). Fall back to /admin/users for admins.
        let users = [];
        try {
          users = (await api.get("/users/roster")).data || [];
        } catch {
          users = (await api.get("/admin/users")).data || [];
        }
        setSalespeople(users.filter((u) => (u.role || "").toLowerCase() === "sales"));
        setTechnicians(users.filter((u) => (u.role || "").toLowerCase() === "technician"));
      } catch (e) { /* noop */ }
      // Load customers + body shops for the "existing contact" picker.
      // We pull from BOTH master_customers (via /master/customers) AND
      // /crm/contacts to guarantee no customer is missing from the picker
      // even if the two collections have drifted.
      try {
        const [crmRes, masterCustRes, shopsRes] = await Promise.all([
          api.get("/crm/contacts?type=customer"),
          api.get("/master/customers"),
          api.get("/master/body-shops"),
        ]);
        const seen = new Set();
        const push = (arr, mapFn) => arr.forEach((r) => {
          const item = mapFn(r);
          if (!item || !item.id || seen.has(item.id)) return;
          seen.add(item.id);
          return items.push(item);
        });
        const items = [];
        push((shopsRes.data || []).filter((s) => !s.archived), (s) => ({
          id: s.id, kind: "body_shop", name: s.company_name,
          email: s.email, phone: s.phone,
          address: [s.address, s.city, s.state, s.zip_code].filter(Boolean).join(", "),
        }));
        push((crmRes.data || []), (c) => ({
          id: c.id, kind: "customer", name: c.name,
          email: c.email, phone: c.phone, address: c.address,
        }));
        // Fallback: customers only in master_customers (drift). Show them too.
        push((masterCustRes.data || []).filter((c) => !c.archived), (c) => ({
          id: c.id, kind: "customer",
          name: [c.first_name, c.last_name].filter(Boolean).join(" ") || c.name || "—",
          email: c.email, phone: c.phone,
          address: [c.address, c.city, c.state, c.zip_code].filter(Boolean).join(", "),
        }));
        setContactOptions(items);
      } catch (e) { /* noop */ }
      if (isEdit) {
        try {
          const q = (await api.get(`/quotes/${id}`)).data;
          setQuoteRef(q);
          setForm({
            vin: q.vin || "",
            vehicle_year: q.vehicle_year || "",
            vehicle_make: q.vehicle_make || "",
            vehicle_model: q.vehicle_model || "",
            customer_name: q.customer_name || "",
            customer_email: q.customer_email || "",
            customer_phone: q.customer_phone || "",
            customer_address: q.customer_address || "",
            is_body_shop: !!q.is_body_shop,
            linked_customer_id: q.linked_customer_id || "",
            linked_body_shop_id: q.linked_body_shop_id || "",
            tax_pct: q.tax_pct || 0,
            notes: q.notes || "",
            salesperson_id: q.salesperson_id || "",
            salesperson_name: q.salesperson_name || "",
            commission_pct: q.commission_pct ?? 5,
            scheduled_at: q.scheduled_at ? q.scheduled_at.slice(0, 16) : "",
            technician_id: q.technician_id || "",
            technician_name: q.technician_name || "",
            status: q.status || "draft",
          });
          const sel = {};
          (q.lines || []).forEach((l) => { sel[l.op_key] = { cal_type: l.cal_type, unit_price: l.unit_price, amount: l.amount }; });
          setSelected(sel);
          setLoading(false);
        } catch (e) { toast.error("Failed to load quote"); setLoading(false); }
      }
    })();
    /* eslint-disable-next-line */
  }, [id]);

  // ---- VIN decode ----
  const decodeVin = async () => {
    const v = (vinInput || form.vin).trim().toUpperCase();
    if (v.length < 11) return toast.error("Invalid VIN (min 11 chars)");
    setDecoding(true);
    try {
      const r = await api.get(`/vin/decode/${v}`);
      setDecoded(r.data);
      setForm((f) => ({
        ...f,
        vin: v,
        vehicle_year: r.data?.year || f.vehicle_year,
        vehicle_make: r.data?.make || f.vehicle_make,
        vehicle_model: r.data?.model || f.vehicle_model,
      }));
      toast.success("VIN decoded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Decode failed");
    }
    setDecoding(false);
  };

  // ---- Suggest ops based on decoded ADAS systems ----
  const suggestedOps = useMemo(() => {
    if (!decoded?.adas_systems) return new Set();
    const map = {
      front_camera:    "windshield_camera",
      front_radar_acc: "front_radar",
      rear_radar_bsm:  "blind_spot_radar",
      surround_view_360: "surround_view_monitor",
      adaptive_headlamps: "night_vision",
      steering_angle_sensor: "module_programming",
    };
    const set = new Set();
    Object.entries(decoded.adas_systems).forEach(([k, v]) => { if (v && map[k]) set.add(map[k]); });
    return set;
  }, [decoded]);

  // Merged operations list: 10 standard ADAS + saved custom services from the catalog
  const opsList = useMemo(() => {
    const std = (catalog.operations || []).map((op) => ({ ...op, isCustom: false }));
    const custom = (customServices || []).map((cs) => ({
      key: `custom_${cs.id}`,
      label: cs.name,
      isCustom: true,
      default_price: cs.default_price,
      custom_service_id: cs.id,
    }));
    return [...std, ...custom];
  }, [catalog.operations, customServices]);

  // ---- Toggle op ----
  const toggleOp = (opKey) => {
    setSelected((s) => {
      if (s[opKey]) {
        const next = { ...s };
        delete next[opKey];
        return next;
      }
      const op = opsList.find((o) => o.key === opKey);
      if (op?.isCustom) {
        const up = op.default_price || 0;
        return { ...s, [opKey]: { cal_type: null, unit_price: up, amount: up, op_label: op.label, is_custom: true } };
      }
      const prices = catalog.prices[opKey] || { static: 0, dynamic: 0, dual: 0 };
      const calType = "static";
      const unitPrice = prices[calType] ?? 0;
      return { ...s, [opKey]: { cal_type: calType, unit_price: unitPrice, amount: unitPrice } };
    });
  };

  const setCalType = (opKey, calType) => {
    setSelected((s) => {
      const prices = catalog.prices[opKey] || {};
      const up = prices[calType] ?? 0;
      return { ...s, [opKey]: { ...(s[opKey] || {}), cal_type: calType, unit_price: up, amount: up } };
    });
  };

  const setLinePrice = (opKey, price) => {
    const v = parseFloat(price) || 0;
    setSelected((s) => ({ ...s, [opKey]: { ...(s[opKey] || {}), unit_price: v, amount: v } }));
  };

  // ---- Manual service entry ----
  const submitManualService = () => {
    const name = manualName.trim();
    const price = parseFloat(manualPrice) || 0;
    if (!name) return toast.error("Nome do serviço obrigatório");
    if (price <= 0) return toast.error("Preço deve ser maior que zero");
    setPendingCustom({ name, price });
    setSaveDialogOpen(true);
  };

  const confirmAddOnly = () => {
    // Just add as a one-time line in this quote — not saved permanently
    const key = `oneoff_${Date.now()}`;
    setSelected((s) => ({
      ...s,
      [key]: {
        cal_type: null,
        unit_price: pendingCustom.price,
        amount: pendingCustom.price,
        op_label: pendingCustom.name,
        is_custom: true,
        is_oneoff: true,
      },
    }));
    setSaveDialogOpen(false);
    setPendingCustom(null);
    setManualName(""); setManualPrice("");
    toast.success("Serviço adicionado a este orçamento");
  };

  const confirmSaveToList = async () => {
    try {
      const r = await api.post("/quotes/custom-services", {
        name: pendingCustom.name,
        default_price: pendingCustom.price,
      });
      setCustomServices((cs) => [r.data, ...cs]);
      const key = `custom_${r.data.id}`;
      setSelected((s) => ({
        ...s,
        [key]: {
          cal_type: null,
          unit_price: pendingCustom.price,
          amount: pendingCustom.price,
          op_label: pendingCustom.name,
          is_custom: true,
        },
      }));
      setSaveDialogOpen(false);
      setPendingCustom(null);
      setManualName(""); setManualPrice("");
      toast.success("Serviço salvo na lista e adicionado ao orçamento");
    } catch (e) {
      toast.error("Falha ao salvar serviço");
    }
  };

  const removeCustomFromList = async (customServiceId) => {
    if (!window.confirm("Remover este serviço da lista permanentemente? (não afeta orçamentos já criados)")) return;
    try {
      await api.delete(`/quotes/custom-services/${customServiceId}`);
      setCustomServices((cs) => cs.filter((x) => x.id !== customServiceId));
      // Also uncheck it if it was selected
      setSelected((s) => {
        const next = { ...s };
        delete next[`custom_${customServiceId}`];
        return next;
      });
      toast.success("Removido");
    } catch (e) { toast.error("Falha ao remover"); }
  };

  const totals = useMemo(() => {
    const subtotal = Object.values(selected).reduce((sum, l) => sum + (l.amount || 0), 0);
    const tax = subtotal * (parseFloat(form.tax_pct) || 0) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [selected, form.tax_pct]);

  // ---- Save ----
  const buildPayload = () => {
    const lines = Object.entries(selected).map(([opKey, l]) => {
      const meta = opsList.find((op) => op.key === opKey);
      return {
        op_key: opKey,
        op_label: meta ? meta.label : (l.op_label || opKey),
        cal_type: l.cal_type || "static",
        unit_price: l.unit_price,
        quantity: 1,
        amount: l.amount,
      };
    });
    const salesUser = salespeople.find((u) => u.id === form.salesperson_id);
    const techUser = technicians.find((u) => u.id === form.technician_id);
    return {
      ...form,
      vin: (form.vin || "").toUpperCase() || null,
      vehicle_year: form.vehicle_year ? parseInt(form.vehicle_year) : null,
      tax_pct: parseFloat(form.tax_pct) || 0,
      commission_pct: parseFloat(form.commission_pct) || 0,
      salesperson_name: salesUser?.name || form.salesperson_name || null,
      technician_name: techUser?.name || form.technician_name || null,
      scheduled_at: form.scheduled_at || null,
      lines,
    };
  };

  const validate = () => {
    if (!form.customer_name.trim()) { toast.error("Customer name required"); return false; }
    if (Object.keys(selected).length === 0) { toast.error("Select at least one ADAS operation"); return false; }
    return true;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/quotes/${id}`, buildPayload());
        toast.success("Saved");
      } else {
        const r = await api.post("/quotes", buildPayload());
        toast.success("Quote created");
        nav(`/quotes/${r.data.id}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const closeQuote = async () => {
    if (!isEdit) { toast.error("Save first"); return; }
    if (!validate()) return;
    if (!window.confirm("Fechar o orçamento? Isso vai:\n\n• Cadastrar o cliente\n• Gerar uma invoice\n• Criar uma ordem de serviço\n• Registrar comissão do vendedor")) return;
    setSaving(true);
    try {
      // Save first to persist any changes
      await api.patch(`/quotes/${id}`, buildPayload());
      const r = await api.post(`/quotes/${id}/close`);
      const wo = r.data.calibration_report_id;
      const j = r.data.job_id;
      toast.success(`Fechado! Invoice ${r.data.invoice_number}${wo ? ` · WO #${wo.slice(0,8)}` : ""} criada.`, {
        description: "Work Order na fila do técnico. Você será redirecionado.",
        duration: 5000,
      });
      // Prefer work-orders detail page so the tech instructions are immediately visible
      if (wo) {
        nav(`/work-orders/${wo}`);
      } else if (j) {
        nav(`/jobs/${j}`);
      } else {
        nav(`/quotes/${id}`);
        window.location.reload();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Close failed"); }
    setSaving(false);
  };

  const requestSignature = async () => {
    if (!isEdit) { toast.error("Salve o orçamento primeiro"); return; }
    try {
      const r = await api.post(`/quotes/${id}/issue-sign-token`);
      const url = `${window.location.origin}/sign/${r.data.sign_token}`;
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      toast.success("Signature link copied to clipboard", {
        description: url,
        duration: 8000,
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to issue link"); }
  };

  const printQuote = async () => {
    if (!isEdit) { toast.error("Salve o orçamento primeiro para gerar o PDF"); return; }
    try {
      const r = await api.get(`/quotes/${id}/pdf`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `quote-${quoteRef?.quote_number || id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 2000);
      toast.success("PDF baixado");
    } catch (e) { toast.error("Falha ao gerar PDF"); }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Excluir este orçamento?")) return;
    try { await api.delete(`/quotes/${id}`); toast.success("Excluído"); nav("/quotes"); }
    catch (e) { toast.error("Falhou"); }
  };

  if (loading) return <div className="text-center text-slate-500 py-20">Loading...</div>;

  const isClosed = form.status === "closed";

  return (
    <div className="space-y-6 animate-slide-up print:space-y-3" data-testid="quote-new-page">
      {/* Header */}
      <div className="flex items-center justify-between print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => nav("/quotes")} className="border-slate-700"><ArrowLeft className="w-4 h-4 mr-1" />Voltar</Button>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold">Sales</div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              {isEdit ? (quoteRef?.quote_number || "Orçamento") : "Novo Orçamento"}
              {isClosed && <span className="ml-3 text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 uppercase tracking-wider">Fechado</span>}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && !isClosed && (
            <Button variant="outline" size="sm" onClick={remove} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10" data-testid="delete-quote-btn"><Trash2 className="w-3.5 h-3.5" /></Button>
          )}
          <Button variant="outline" size="sm" onClick={printQuote} className="border-slate-700" data-testid="print-quote-btn"><Download className="w-3.5 h-3.5 mr-1.5" />Imprimir Orçamento</Button>
          {isEdit && !isClosed && (
            <Button variant="outline" size="sm" onClick={requestSignature} className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10" data-testid="request-signature-btn" title="Copy a magic link the customer can use to sign this quote from any device">
              <PenTool className="w-3.5 h-3.5 mr-1.5" />Solicitar Assinatura
            </Button>
          )}
          {isEdit && !isClosed && (
            <Button size="sm" onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-500" data-testid="save-quote-btn"><Save className="w-3.5 h-3.5 mr-1.5" />Salvar</Button>
          )}
          {!isEdit && (
            <Button size="sm" onClick={save} disabled={saving} className="bg-purple-600 hover:bg-purple-500" data-testid="save-quote-btn"><Save className="w-3.5 h-3.5 mr-1.5" />Criar Orçamento</Button>
          )}
          {isEdit && !isClosed && (
            <Button size="sm" onClick={closeQuote} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="close-quote-btn"><CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />Fechar Orçamento</Button>
          )}
        </div>
      </div>

      {isClosed && (quoteRef?.converted_invoice_id || quoteRef?.converted_job_id) && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-200 print:hidden">
          Orçamento fechado com sucesso! Invoice, ordem de serviço e comissão foram criadas automaticamente.
        </div>
      )}

      {quoteRef?.signed_at && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-200 print:hidden flex items-center gap-2" data-testid="quote-signed-banner">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>
            Assinado eletronicamente por <strong>{quoteRef.signer_name}</strong> em {new Date(quoteRef.signed_at).toLocaleString()}
            {quoteRef.signature_ip ? ` · IP ${quoteRef.signature_ip}` : ""}
          </span>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* LEFT — VIN + ADAS ops */}
        <div className="lg:col-span-2 space-y-6">
          {/* VIN decoder card */}
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 print:hidden">
            <div className="flex items-center gap-2 mb-3">
              <Car className="w-4 h-4 text-blue-400" />
              <h3 className="font-display text-lg font-medium">1. Veículo (VIN)</h3>
            </div>
            <div className="flex items-center gap-2">
              <Input data-testid="vin-input" value={vinInput || form.vin} onChange={(e) => setVinInput(e.target.value.toUpperCase())} placeholder="17-character VIN" className="bg-[#030712] border-slate-800 font-mono uppercase" maxLength={17} disabled={isClosed} />
              <Button data-testid="decode-vin-btn" onClick={decodeVin} disabled={decoding || isClosed} className="bg-blue-600 hover:bg-blue-500">
                <Search className="w-4 h-4 mr-1.5" /> {decoding ? "Decoding..." : "Decodificar"}
              </Button>
            </div>
            {(form.vehicle_year || form.vehicle_make || form.vehicle_model) && (
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Year</Label>
                  <Input value={form.vehicle_year || ""} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} /></div>
                <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Make</Label>
                  <Input value={form.vehicle_make || ""} onChange={(e) => setForm({ ...form, vehicle_make: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} /></div>
                <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Model</Label>
                  <Input value={form.vehicle_model || ""} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} /></div>
              </div>
            )}
            {decoded?.adas_systems && (
              <div className="mt-4 pt-3 border-t border-slate-800">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3 h-3" />ADAS Equipment (Inferred)
                </div>
                <div className="grid sm:grid-cols-2 gap-1.5">
                  {Object.entries(decoded.adas_systems).map(([key, equipped]) => {
                    const meta = ADAS_LABELS[key] || { name: key, icon: "•" };
                    return (
                      <div
                        key={key}
                        data-testid={`adas-${key}`}
                        className={`flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg ${equipped ? "bg-emerald-500/5 border border-emerald-500/15" : "bg-slate-900/40 border border-slate-800/40"}`}
                      >
                        <span className={equipped ? "text-slate-200" : "text-slate-600 line-through"}>
                          {meta.icon} {meta.name}
                        </span>
                        <span className={`text-[9px] uppercase tracking-wider font-bold ${equipped ? "text-emerald-400" : "text-slate-600"}`}>
                          {equipped ? "Yes" : "No"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ADAS ops picker */}
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-400" />
                <h3 className="font-display text-lg font-medium">2. Operações ADAS</h3>
              </div>
              <div className="text-[10px] text-slate-500">Preços da rate table: <span className="text-slate-300">{catalog.rate_table_name || "Defaults"}</span></div>
            </div>
            <div className="space-y-1.5">
              {opsList.map((op) => {
                const isSel = !!selected[op.key];
                const isSuggested = suggestedOps.has(op.key);
                const line = selected[op.key] || {};
                return (
                  <div key={op.key} className={`rounded-md border transition ${isSel ? "border-purple-500/40 bg-purple-500/5" : "border-slate-800 bg-slate-900/20 hover:bg-slate-900/40"}`}>
                    <div className="flex items-center gap-3 px-3 py-2">
                      <input data-testid={`op-check-${op.key}`} type="checkbox" checked={isSel} onChange={() => !isClosed && toggleOp(op.key)} disabled={isClosed} className="w-4 h-4" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-100 flex items-center gap-2">
                          {op.label}
                          {op.isCustom && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30 text-amber-300 uppercase tracking-wider">Custom</span>}
                          {isSuggested && !isSel && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 border border-blue-500/30 text-blue-300 uppercase tracking-wider">Sugerido</span>}
                        </div>
                      </div>
                      {op.isCustom && !isSel && !isClosed && (
                        <button
                          data-testid={`custom-op-remove-${op.custom_service_id}`}
                          onClick={() => removeCustomFromList(op.custom_service_id)}
                          className="text-slate-600 hover:text-rose-400 p-1"
                          title="Remover da lista"
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                      {isSel && (
                        <>
                          {!op.isCustom && (
                            <div className="flex items-center gap-1">
                              {CAL_TYPES.map((t) => (
                                <button
                                  key={t.key}
                                  data-testid={`op-type-${op.key}-${t.key}`}
                                  onClick={() => !isClosed && setCalType(op.key, t.key)}
                                  disabled={isClosed}
                                  className={`px-2 py-1 text-[10px] uppercase tracking-wider font-bold rounded border transition ${line.cal_type === t.key ? `${t.color} border-current` : "text-slate-500 border-slate-800 hover:text-slate-300"}`}
                                >{t.label}</button>
                              ))}
                            </div>
                          )}
                          <div className="w-24">
                            <Input
                              data-testid={`op-price-${op.key}`}
                              type="number"
                              step="0.01"
                              value={line.unit_price ?? ""}
                              onChange={(e) => setLinePrice(op.key, e.target.value)}
                              className="bg-[#030712] border-slate-800 font-mono text-right text-sm h-8"
                              disabled={isClosed}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Manual entry — add extra service on the fly */}
            {!isClosed && (
              <div className="mt-4 rounded-md border border-dashed border-amber-500/30 bg-amber-500/5 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-amber-400 mb-2 flex items-center gap-1.5">
                  <Plus className="w-3 h-3" /> Adicionar Serviço Manual
                </div>
                <div className="grid grid-cols-[1fr_140px_auto] gap-2 items-end">
                  <Input
                    data-testid="manual-name-input"
                    value={manualName}
                    onChange={(e) => setManualName(e.target.value)}
                    placeholder="Ex: Diagnóstico OBD, Reset SAS, Test Drive..."
                    className="bg-[#030712] border-slate-800"
                  />
                  <Input
                    data-testid="manual-price-input"
                    type="number"
                    step="0.01"
                    value={manualPrice}
                    onChange={(e) => setManualPrice(e.target.value)}
                    placeholder="Preço"
                    className="bg-[#030712] border-slate-800 font-mono text-right"
                  />
                  <Button data-testid="manual-add-btn" size="sm" onClick={submitManualService} className="bg-amber-600 hover:bg-amber-500">
                    <Plus className="w-4 h-4 mr-1" /> Adicionar
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
            <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Notas / Observações</Label>
            <textarea data-testid="notes-input" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1.5 w-full bg-[#030712] border border-slate-800 rounded-md p-2 text-sm text-slate-200" disabled={isClosed} />
          </div>
        </div>

        {/* RIGHT — customer + salesperson + totals */}
        <div className="space-y-6">
          {/* Customer */}
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
            <div className="flex items-center gap-2 mb-3">
              <User className="w-4 h-4 text-amber-400" />
              <h3 className="font-display text-lg font-medium">3. Cliente</h3>
              {form.is_body_shop && (
                <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded-full px-2 py-0.5">Body Shop</span>
              )}
            </div>

            {/* Existing contact picker */}
            {!isClosed && (
              <div className="mb-3 rounded-lg border border-slate-800 bg-[#030712] p-2">
                <button
                  type="button"
                  onClick={() => setContactPickerOpen((v) => !v)}
                  className="w-full text-left text-xs text-slate-400 hover:text-slate-200 flex items-center justify-between px-2 py-1.5"
                  data-testid="contact-picker-toggle"
                >
                  <span>{form.linked_customer_id || form.linked_body_shop_id ? "✓ Contato existente selecionado" : "Buscar cliente ou body shop já cadastrado..."}</span>
                  <Search className="w-3.5 h-3.5" />
                </button>
                {contactPickerOpen && (
                  <div className="mt-2 border-t border-slate-800 pt-2" data-testid="contact-picker-list">
                    <Input
                      autoFocus
                      placeholder="Buscar por nome, email ou telefone..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      className="bg-[#030712] border-slate-800 h-8 text-sm mb-2"
                      data-testid="contact-picker-search"
                    />
                    <div className="max-h-56 overflow-auto space-y-1">
                      {contactOptions
                        .filter((c) => {
                          const q = contactSearch.trim().toLowerCase();
                          if (!q) return true;
                          return (c.name || "").toLowerCase().includes(q)
                            || (c.email || "").toLowerCase().includes(q)
                            || (c.phone || "").toLowerCase().includes(q);
                        })
                        .slice(0, 25)
                        .map((c) => (
                          <button
                            key={`${c.kind}-${c.id}`}
                            type="button"
                            onClick={() => {
                              setForm({
                                ...form,
                                customer_name: c.name,
                                customer_email: c.email || "",
                                customer_phone: c.phone || "",
                                customer_address: c.address || "",
                                is_body_shop: c.kind === "body_shop",
                                linked_customer_id: c.kind === "customer" ? c.id : "",
                                linked_body_shop_id: c.kind === "body_shop" ? c.id : "",
                              });
                              setContactPickerOpen(false);
                              setContactSearch("");
                              toast.success(`${c.kind === "body_shop" ? "Body Shop" : "Cliente"} selecionado`);
                            }}
                            className="w-full text-left rounded-md border border-slate-800 bg-[#0B1120] hover:border-blue-500/40 hover:bg-blue-500/5 px-2.5 py-1.5 text-xs"
                            data-testid="contact-option"
                          >
                            <div className="flex items-center gap-2">
                              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${c.kind === "body_shop" ? "bg-blue-500/20 text-blue-300" : "bg-emerald-500/20 text-emerald-300"}`}>
                                {c.kind === "body_shop" ? "Body Shop" : "Cliente"}
                              </span>
                              <span className="text-slate-200 font-medium">{c.name}</span>
                            </div>
                            {(c.email || c.phone) && (
                              <div className="text-[10px] text-slate-500 mt-0.5 truncate">
                                {[c.email, c.phone].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </button>
                        ))}
                      {contactOptions.length === 0 && (
                        <div className="text-[11px] text-slate-500 px-1 py-1">Nenhum contato cadastrado ainda.</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* is body shop toggle */}
            {!isClosed && (
              <label className="flex items-center gap-2 mb-3 rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 cursor-pointer" data-testid="is-body-shop-toggle-label">
                <input
                  type="checkbox"
                  checked={form.is_body_shop}
                  onChange={(e) => setForm({ ...form, is_body_shop: e.target.checked, linked_customer_id: e.target.checked ? "" : form.linked_customer_id })}
                  className="w-4 h-4 accent-blue-500"
                  data-testid="is-body-shop-checkbox"
                />
                <span className="text-xs text-slate-200">
                  Este cliente é um <strong>Body Shop</strong>
                  <span className="block text-[10px] text-slate-500 mt-0.5">Ao fechar o orçamento, também será criado na lista de Body Shops (Master Data + CRM)</span>
                </span>
              </label>
            )}

            <div className="space-y-3">
              <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Nome *</Label>
                <Input data-testid="customer-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value, linked_customer_id: "", linked_body_shop_id: "" })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} /></div>
              <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Email</Label>
                <Input data-testid="customer-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} /></div>
              <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Telefone</Label>
                <Input data-testid="customer-phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} /></div>
              <div><Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Endereço</Label>
                <Input data-testid="customer-address" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} /></div>
            </div>
          </div>

          {/* Salesperson + Schedule */}
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5 print:hidden">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-emerald-400" />
              <h3 className="font-display text-lg font-medium">4. Vendedor & Agenda</h3>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Vendedor</Label>
                <select data-testid="salesperson-select" value={form.salesperson_id} onChange={(e) => setForm({ ...form, salesperson_id: e.target.value })} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-md h-9 px-3 text-sm text-slate-200" disabled={isClosed}>
                  <option value="">-- selecione --</option>
                  {salespeople.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Comissão (%)</Label>
                <Input data-testid="commission-pct" type="number" step="0.5" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} />
              </div>
              <div className="border-t border-slate-800 pt-3">
                <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Data agendada (visita técnica)</Label>
                <Input data-testid="scheduled-at" type="datetime-local" value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1 bg-[#030712] border-slate-800" disabled={isClosed} />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Técnico</Label>
                <select data-testid="technician-select" value={form.technician_id} onChange={(e) => setForm({ ...form, technician_id: e.target.value })} className="mt-1 w-full bg-[#030712] border border-slate-800 rounded-md h-9 px-3 text-sm text-slate-200" disabled={isClosed}>
                  <option value="">-- selecione --</option>
                  {technicians.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Totals */}
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-amber-400" />
              <h3 className="font-display text-lg font-medium">Total</h3>
            </div>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span className="font-mono">${totals.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400">Tax (%)</span>
                <Input type="number" step="0.01" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} className="w-16 bg-[#030712] border-slate-800 text-right font-mono h-6 text-xs" disabled={isClosed} />
              </div>
              <div className="flex justify-between"><span className="text-slate-400">Tax</span><span className="font-mono">${totals.tax.toFixed(2)}</span></div>
              <div className="flex justify-between border-t border-amber-500/20 pt-2 mt-2 text-base font-medium">
                <span>Total</span>
                <span className="font-mono text-amber-300" data-testid="quote-total">${totals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Manual service confirmation dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-amber-400" /> Como quer adicionar este serviço?
            </DialogTitle>
          </DialogHeader>
          {pendingCustom && (
            <div className="space-y-4 mt-2" data-testid="manual-confirm-dialog">
              <div className="rounded-md border border-slate-800 bg-slate-900/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Serviço</div>
                <div className="mt-0.5 text-base font-medium text-slate-100">{pendingCustom.name}</div>
                <div className="text-xs text-amber-300 font-mono mt-1">${pendingCustom.price.toFixed(2)}</div>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <button
                  data-testid="confirm-oneoff-btn"
                  onClick={confirmAddOnly}
                  className="w-full text-left rounded-md border border-slate-700 bg-slate-900/40 hover:bg-slate-800/60 p-3 transition"
                >
                  <div className="text-sm font-medium text-slate-100">Só para este orçamento</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Adiciona uma linha só neste QT-XXX. Não fica salvo pra próxima vez.</div>
                </button>
                <button
                  data-testid="confirm-savelist-btn"
                  onClick={confirmSaveToList}
                  className="w-full text-left rounded-md border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 p-3 transition"
                >
                  <div className="text-sm font-medium text-amber-200">Salvar na lista de serviços</div>
                  <div className="text-[11px] text-amber-100/50 mt-0.5">Aparece como opção em todos os próximos orçamentos. Você pode remover depois.</div>
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
